/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import PulseLoader from '../components/loading/PulseLoader';
import Render from '../render/Render';
import {dlsColor} from '../theme/theme';
import FilterTreeNodeIcon from './FilterTreeNodeIcon';
import {FilterTreeSearchResultBase} from './types';

export interface FilterTreeSearchResultsProps {
  loading?: boolean;
  loadingLabel: string;
  noResultsLabel: string;
  onSelect: (result: FilterTreeSearchResultBase) => void;
  results: FilterTreeSearchResultBase[];
}

const useStyles = makeStyles(() => ({
  list : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    width         : '100%'
  },
  row : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    boxSizing       : 'border-box',
    cursor          : 'pointer',
    display         : 'flex',
    gap             : 8,
    padding         : '8px 16px',
    textAlign       : 'left',
    width           : '100%',
    '&:hover'       : {
      backgroundColor : dlsColor('grey6')
    }
  },
  icon : {
    alignItems     : 'center',
    display        : 'flex',
    flexShrink     : 0,
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  labels : {
    display       : 'flex',
    flexDirection : 'column',
    minWidth      : 0
  },
  label : {
    color        : dlsColor('grey1'),
    fontFamily   : 'Inter',
    fontSize     : 14,
    fontWeight   : 500,
    lineHeight   : '20px',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  },
  groupLabel : {
    color        : dlsColor('grey3'),
    fontFamily   : 'Inter',
    fontSize     : 12,
    lineHeight   : '16px',
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap'
  },
  empty : {
    color      : dlsColor('grey3'),
    fontFamily : 'Inter',
    fontSize   : 14,
    lineHeight : '20px',
    padding    : '8px 16px 16px',
    width      : '100%'
  },
  loading : {
    alignItems     : 'center',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 44,
    justifyContent : 'center',
    padding        : '0 16px',
    width          : '100%'
  }
}));

const FilterTreeSearchResultRow = ({
  onSelect,
  result
}: {
  onSelect: (result: FilterTreeSearchResultBase) => void;
  result: FilterTreeSearchResultBase;
}) => {
  const classes = useStyles();

  const handleClick = useCallback(function() {
    onSelect(result);
  }, [onSelect, result]);

  return (
    <button type='button'
      className={classes.row}
      onClick={handleClick}>
      <span className={classes.icon}>
        <FilterTreeNodeIcon isAsset={result.isAsset} />
      </span>
      <span className={classes.labels}>
        <span className={classes.label}>
          {result.label}
        </span>
        <Render.If condition={result.groupLabel != null}>
          <span className={classes.groupLabel}>
            {result.groupLabel}
          </span>
        </Render.If>
      </span>
    </button>
  );
};

/**
 * Displays a flat list of tree checklist search results, distinct from the
 * tree itself - picking a result reveals it in the tree instead of pruning
 * the tree in place.
 *
 * ```typescript
 * import FilterTreeSearchResults from '@ion/dls/src/filters/FilterTreeSearchResults';
 *
 * ...
 *
 * <FilterTreeSearchResults results={searchResults}
 *   loadingLabel='Loading'
 *   noResultsLabel='No results found'
 *   onSelect={onSelectSearchResult} />
 * ```
 */
export const FilterTreeSearchResults = ({
  loading = false,
  loadingLabel,
  noResultsLabel,
  onSelect,
  results
}: FilterTreeSearchResultsProps) => {
  const classes = useStyles();

  return (
    <div className={classes.list} role='listbox'>
      <Render>
        <Render.If condition={loading}>
          <div className={classes.loading}>
            <PulseLoader message={loadingLabel} />
          </div>
        </Render.If>
        <Render.ElseIf condition={!results.length}>
          <div className={classes.empty} role='status'>
            {noResultsLabel}
          </div>
        </Render.ElseIf>
        <Render.Else>
          {results.map(result => (
            <FilterTreeSearchResultRow key={result.key}
              onSelect={onSelect}
              result={result} />
          ))}
        </Render.Else>
      </Render>
    </div>
  );
};

export default memo(FilterTreeSearchResults);
